import { Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApp } from '@/state/AppContext';

export function FilterBar() {
  const { state, setFilters, clearFilters } = useApp();
  const { filters } = state;
  const [searchDraft, setSearchDraft] = useState(filters.q);
  const [tagDraft, setTagDraft] = useState('');

  // Debounce the search input by 300ms.
  useEffect(() => {
    const id = setTimeout(() => {
      if (searchDraft !== filters.q) setFilters({ q: searchDraft });
    }, 300);
    return () => clearTimeout(id);
  }, [searchDraft, filters.q, setFilters]);

  const addTag = () => {
    const tag = tagDraft.trim().toLowerCase();
    if (!tag) return;
    if (filters.tags.includes(tag)) {
      setTagDraft('');
      return;
    }
    setFilters({ tags: [...filters.tags, tag] });
    setTagDraft('');
  };

  const removeTag = (tag: string) => {
    setFilters({ tags: filters.tags.filter((t) => t !== tag) });
  };

  return (
    <div className="card-edge bg-card rounded-lg p-3 flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1.5 grow min-w-[240px]">
        <Label htmlFor="filter-q">Search</Label>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            id="filter-q"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="title, description, notes…"
            className="pl-8"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5 grow min-w-[240px]">
        <Label htmlFor="filter-tag">Tags</Label>
        <div className="flex items-center gap-2 flex-wrap">
          {filters.tags.map((t) => (
            <Badge key={t} variant="secondary" className="gap-1">
              {t}
              <button
                type="button"
                aria-label={`Remove tag ${t}`}
                onClick={() => removeTag(t)}
                className="opacity-70 hover:opacity-100"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Input
            id="filter-tag"
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder="add tag…"
            className="h-8 w-32"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="filter-from">From</Label>
        <Input
          id="filter-from"
          type="date"
          value={filters.from ? filters.from.slice(0, 10) : ''}
          onChange={(e) =>
            setFilters({ from: e.target.value ? `${e.target.value}T00:00:00.000Z` : '' })
          }
          className="h-9"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="filter-to">To</Label>
        <Input
          id="filter-to"
          type="date"
          value={filters.to ? filters.to.slice(0, 10) : ''}
          onChange={(e) =>
            setFilters({ to: e.target.value ? `${e.target.value}T23:59:59.999Z` : '' })
          }
          className="h-9"
        />
      </div>

      <div className="flex items-center gap-2 pb-1">
        <Checkbox
          id="filter-unassigned"
          checked={filters.unassigned}
          onCheckedChange={(v) => setFilters({ unassigned: v === true })}
        />
        <Label htmlFor="filter-unassigned" className="cursor-pointer">
          Only unassigned
        </Label>
      </div>

      <Button
        variant="ghost"
        onClick={() => {
          clearFilters();
          setSearchDraft('');
          setTagDraft('');
        }}
      >
        Clear
      </Button>
    </div>
  );
}
